/**
 * Benachrichtigungs-Einstellungen Modal
 * Erreichbar über Glocken-Icon in der Sidebar.
 */

import { useState, useEffect } from 'react';
import {
  Bell, X, Mail, Smartphone, Calendar, Users, 
  Filter, Save, Loader2, Check, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export function BenachrichtigungsModal({ isOpen, onClose, benutzerId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [klienten, setKlienten] = useState([]);
  const [selectedKlienten, setSelectedKlienten] = useState(new Set());
  const [showKlientenFilter, setShowKlientenFilter] = useState(false);

  useEffect(() => {
    if (isOpen && benutzerId) {
      loadSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, benutzerId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, klientenRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/benutzer/einstellungen/benachrichtigungen?benutzer_id=${benutzerId}`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/benutzer/einstellungen/beleg-erinnerung/klienten?benutzer_id=${benutzerId}`).then(r => r.json())
      ]);
      
      setSettings(settingsRes);
      setKlienten(klientenRes.klienten || []);
      
      // Ausgewählte Klienten setzen
      const filter = settingsRes?.beleg_erinnerung?.klienten_filter;
      if (filter && filter.length > 0) {
        setSelectedKlienten(new Set(filter));
        setShowKlientenFilter(true);
      } else {
        setSelectedKlienten(new Set(klientenRes.klienten?.map(k => k.id) || []));
        setShowKlientenFilter(false);
      }
    } catch (err) {
      toast.error('Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Einstellungen speichern
      await fetch(`${BACKEND_URL}/api/benutzer/einstellungen/beleg-erinnerung?benutzer_id=${benutzerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings.beleg_erinnerung)
      });
      
      // Klienten-Filter speichern
      if (showKlientenFilter) {
        await fetch(`${BACKEND_URL}/api/benutzer/einstellungen/beleg-erinnerung/klienten?benutzer_id=${benutzerId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ klient_ids: Array.from(selectedKlienten) })
        });
      } else {
        await fetch(`${BACKEND_URL}/api/benutzer/einstellungen/beleg-erinnerung/klienten?benutzer_id=${benutzerId}&alle_klienten=true`, {
          method: 'POST'
        });
      }
      
      toast.success('Einstellungen gespeichert');
      onClose();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      beleg_erinnerung: {
        ...prev.beleg_erinnerung,
        [key]: value
      }
    }));
  };

  const toggleKlient = (klientId) => {
    setSelectedKlienten(prev => {
      const newSet = new Set(prev);
      if (newSet.has(klientId)) {
        newSet.delete(klientId);
      } else {
        newSet.add(klientId);
      }
      return newSet;
    });
  };

  const selectAllKlienten = () => {
    setSelectedKlienten(new Set(klienten.map(k => k.id)));
  };

  const deselectAllKlienten = () => {
    setSelectedKlienten(new Set());
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="benachrichtigungs-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Bell className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800">Benachrichtigungen</h2>
              <p className="text-xs text-slate-500">Beleg-Erinnerungen konfigurieren</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          ) : settings ? (
            <>
              {/* Aktivierung */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Beleg-Erinnerungen</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Benachrichtigung bei fehlenden Belegen
                  </p>
                </div>
                <Switch
                  checked={settings.beleg_erinnerung?.aktiviert ?? true}
                  onCheckedChange={(v) => updateSetting('aktiviert', v)}
                  data-testid="toggle-aktiviert"
                />
              </div>

              {settings.beleg_erinnerung?.aktiviert && (
                <>
                  {/* Kanäle */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-slate-700">Benachrichtigungs-Kanal</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          settings.beleg_erinnerung?.kanal_email 
                            ? 'border-violet-500 bg-violet-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => updateSetting('kanal_email', !settings.beleg_erinnerung?.kanal_email)}
                      >
                        <div className="flex items-center gap-2">
                          <Mail className={`w-5 h-5 ${settings.beleg_erinnerung?.kanal_email ? 'text-violet-600' : 'text-slate-400'}`} />
                          <span className="font-medium">E-Mail</span>
                        </div>
                        {settings.beleg_erinnerung?.kanal_email && (
                          <Check className="w-4 h-4 text-violet-600 mt-2" />
                        )}
                      </div>
                      
                      <div 
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          settings.beleg_erinnerung?.kanal_push 
                            ? 'border-violet-500 bg-violet-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => updateSetting('kanal_push', !settings.beleg_erinnerung?.kanal_push)}
                      >
                        <div className="flex items-center gap-2">
                          <Smartphone className={`w-5 h-5 ${settings.beleg_erinnerung?.kanal_push ? 'text-violet-600' : 'text-slate-400'}`} />
                          <span className="font-medium">Push</span>
                        </div>
                        {settings.beleg_erinnerung?.kanal_push && (
                          <Check className="w-4 h-4 text-violet-600 mt-2" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tag im Monat */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Erinnerung am
                    </Label>
                    <Select
                      value={String(settings.beleg_erinnerung?.tag_im_monat ?? 0)}
                      onValueChange={(v) => updateSetting('tag_im_monat', parseInt(v))}
                    >
                      <SelectTrigger data-testid="select-tag">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Monatsletzter</SelectItem>
                        {[...Array(28)].map((_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>
                            {i + 1}. des Monats
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Schwellwert */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Mindestanzahl fehlender Belege
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={settings.beleg_erinnerung?.schwellwert ?? 1}
                      onChange={(e) => updateSetting('schwellwert', parseInt(e.target.value) || 1)}
                      className="w-24"
                      data-testid="input-schwellwert"
                    />
                    <p className="text-xs text-slate-500">
                      Benachrichtigung nur wenn mindestens {settings.beleg_erinnerung?.schwellwert ?? 1} Beleg(e) fehlen
                    </p>
                  </div>

                  {/* Klienten-Filter */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Klienten-Filter
                      </Label>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={showKlientenFilter}
                          onCheckedChange={setShowKlientenFilter}
                        />
                        <span className="text-xs text-slate-500">
                          {showKlientenFilter ? 'Aktiv' : 'Alle Klienten'}
                        </span>
                      </div>
                    </div>

                    {showKlientenFilter && (
                      <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                        <div className="flex gap-2 mb-2">
                          <Button variant="outline" size="sm" onClick={selectAllKlienten}>
                            Alle auswählen
                          </Button>
                          <Button variant="outline" size="sm" onClick={deselectAllKlienten}>
                            Keine
                          </Button>
                        </div>
                        
                        {klienten.map(k => (
                          <div key={k.id} className="flex items-center gap-2">
                            <Checkbox
                              checked={selectedKlienten.has(k.id)}
                              onCheckedChange={() => toggleKlient(k.id)}
                              id={`klient-${k.id}`}
                            />
                            <label htmlFor={`klient-${k.id}`} className="text-sm cursor-pointer flex-1">
                              {k.name}
                            </label>
                            <Badge variant="outline" className="text-xs">
                              {k.betreuung_status}
                            </Badge>
                          </div>
                        ))}
                        
                        {klienten.length === 0 && (
                          <p className="text-sm text-slate-400 text-center py-2">Keine Klienten gefunden</p>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-slate-500">
                      {showKlientenFilter 
                        ? `${selectedKlienten.size} von ${klienten.length} Klienten ausgewählt`
                        : 'Alle Klienten werden überwacht'}
                    </p>
                  </div>

                  {/* Nur aktive Betreuungen */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="font-medium text-sm">Nur aktive Betreuungen</Label>
                      <p className="text-xs text-slate-500">Archivierte Klienten ignorieren</p>
                    </div>
                    <Switch
                      checked={settings.beleg_erinnerung?.nur_aktive_betreuungen ?? true}
                      onCheckedChange={(v) => updateSetting('nur_aktive_betreuungen', v)}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="text-center text-slate-500 py-8">Keine Einstellungen verfügbar</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="btn-save">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

export default BenachrichtigungsModal;
